/**
 * Field Help — Companies
 *
 * Keys MUST match the company field ids in
 * `features/companies/config/fields/*.ts` (see COMPANY_FIELDS / CompanyFieldId)
 * so the "?" popover attaches to the right column / editor row / card field.
 *
 * Relationship context (used in `relatedTo`): a company has MANY contacts and
 * MANY deals. Deals reach contacts THROUGH the company (deal → company →
 * contact). Bulk email / SMS to every contact at a company is supported. See
 * `lib/actions/relationships/AGENTS.md`.
 */

import type { EntityFieldHelp } from "./types";

export const companyFieldHelp: EntityFieldHelp = {
  name: {
    title: "Name",
    description: "The company / account name. Required on every record.",
    usage: "The headline on every company card, table row, and reminder. Keep it the legal or commonly-used trading name so it dedupes cleanly against QuickBooks and incoming leads.",
    relatedTo: "Anchors the account — its contacts, deals, and tickets all hang off this record.",
  },
  status: {
    title: "Status",
    description: "Lifecycle state of the account (active, prospect, client, former client, suspended, inactive).",
    usage: "Drives quick filters and saved views. Admin-only to change. Move prospects to client when they sign so pipeline and client reporting stay accurate.",
  },
  industry: {
    title: "Industry",
    description: "The company's industry / vertical.",
    usage: "Use for segmenting outreach and tailoring proposals (construction, nonprofit, insurance, etc.). Feeds vertical-based saved views and reporting.",
  },
  website: {
    title: "Website",
    description: "The company's primary website URL.",
    usage: "Starting point for research and enrichment before a call or proposal. Use the full https:// URL.",
  },
  domain: {
    title: "Domain",
    description: "The company's email/web domain (e.g. example.com).",
    usage: "Used to match inbound contacts and communications back to the right account. Keep it the bare domain, no protocol.",
    relatedTo: "Helps auto-associate new contacts whose email ends in this domain.",
  },
  tax_id: {
    title: "Tax ID",
    description: "The company's tax identification number (EIN / equivalent).",
    usage: "Needed for contracts and QuickBooks invoicing. Treat as confidential — it's sensitive client data.",
  },
  address: {
    title: "Address",
    description: "Street address of the company's primary location.",
    usage: "Used on contracts, invoices, and for on-site work. Combine with city/state/zip/country for the full mailing address.",
  },
  city: {
    title: "City",
    description: "City of the company's address.",
    usage: "Part of the full address; also useful for regional segmentation (e.g. Memphis / Mid-South accounts).",
  },
  state: {
    title: "State",
    description: "State / province of the company's address.",
    usage: "Part of the full address; supports regional filtering and tax considerations.",
  },
  zip_code: {
    title: "ZIP Code",
    description: "Postal code of the company's address.",
    usage: "Part of the full mailing address used on invoices and contracts.",
  },
  country: {
    title: "Country",
    description: "Country of the company's address.",
    usage: "Defaults to the US for most WTC accounts; set explicitly for international clients.",
  },
  contract_type: {
    title: "Contract Type",
    description: "The engagement / contract structure for this account.",
    usage: "Admin-only. Captures whether the relationship is retainer, project, contingency, etc. — informs how the account is billed and forecast.",
    relatedTo: "Sets the billing expectation that hourly_rate / monthly_rate fill in.",
  },
  hourly_rate: {
    title: "Hourly Rate",
    description: "Negotiated hourly billing rate for this account, in dollars.",
    usage: "Admin-only. Applied when billing time against this client. Keep it current so time-entry and invoice math is right.",
    relatedTo: "Used alongside logged time entries to bill the client.",
  },
  monthly_rate: {
    title: "Monthly Rate",
    description: "Recurring monthly fee for this account (retainer / MSP), in dollars.",
    usage: "Set for retainer and managed-services clients. Backbone of predictable MSP revenue — keep it accurate for MRR reporting.",
    relatedTo: "Most relevant when Contract Type is a retainer / managed agreement.",
  },
  payment_terms: {
    title: "Payment Terms",
    description: "Agreed payment terms for the account (e.g. Net 15 / Net 30).",
    usage: "Drives invoice due dates and collections follow-up. Set it from the signed contract.",
  },
  organization_revenue: {
    title: "Organization Revenue",
    description: "The client's own annual revenue, in dollars — a sizing signal for the account.",
    usage: "Use to gauge account size and deal potential when qualifying and prioritizing. This is the CLIENT's revenue, not WTC's revenue from them.",
  },
  employee_size: {
    title: "Employee Size",
    description: "Headcount / company-size band for the client organization.",
    usage: "Sizing signal for scoping engagements (support load, licensing, infrastructure). Pairs with Organization Revenue for qualification.",
  },
  current_technologies: {
    title: "Technologies",
    description: "The client's existing tech stack / tools in use.",
    usage: "Research input for proposals and discovery — what they run today shapes migration, cost-optimization, and security recommendations.",
  },
  social_links: {
    title: "Social Links",
    description: "The company's social media profile URLs (LinkedIn, etc.).",
    usage: "Quick links for research and outreach context before a call. Stored as structured data.",
  },
  tags: {
    title: "Tags",
    description: "Free-form labels for segmenting accounts (e.g. partner, vip, priority).",
    usage: "Use for your own segmentation and campaign targeting. Supports bulk editing across many accounts at once.",
  },
  notes: {
    title: "Notes",
    description: "Internal running notes on the account.",
    usage: "Relationship context, account history, internal caveats. Keep enough that a teammate can pick up the account cold.",
  },
  has_active_deals: {
    title: "Has Deals",
    description: "Whether the company has at least one deal in an active pipeline stage.",
    usage: "Read-only — derived automatically from the company's deals. A quick signal for which accounts have live opportunities.",
    relatedTo: "Rolled up from the company's deals (deal stage). A company can have MANY deals.",
  },
  active_deal_count: {
    title: "Active Deals",
    description: "Count of the company's deals currently in active pipeline stages.",
    usage: "Read-only — maintained automatically. Higher counts flag the accounts with the most live opportunity.",
    relatedTo: "Aggregated from the company's deals.",
  },
  total_deal_value: {
    title: "Active Deal Value",
    description: "Sum of revenue across the company's active-stage deals, in dollars.",
    usage: "Read-only — the aggregate value of this account's open pipeline. Use to size and prioritize accounts.",
    relatedTo: "Sum of revenue on the company's active deals (deal → company).",
  },
  last_contacted_at: {
    title: "Last activity",
    description: "Timestamp of the most recent communication with anyone at this account.",
    usage: "Read-only — rolled up from communications across all the company's contacts. A long gap on a client or live prospect is a prompt to reach out.",
    relatedTo: "Latest communication across the company's contacts.",
  },
  quickbooks_customer_ref: {
    title: "QB Customer Ref",
    description: "QuickBooks customer reference ID linked to this company.",
    usage: "Read-only — set by the QuickBooks sync. Links the account to its billing record so invoices map back to the right customer.",
    relatedTo: "Connects the company to its QuickBooks customer for invoicing.",
  },
  quickbooks_customer_name: {
    title: "QB Customer Name",
    description: "Customer name as it appears in QuickBooks.",
    usage: "Read-only — from the QuickBooks sync. If it differs from the company Name, that's a flag the records may need reconciling.",
  },
  quickbooks_sync_at: {
    title: "QB Last Sync",
    description: "When this company last synced with QuickBooks.",
    usage: "Read-only. A stale timestamp can indicate a broken or pending QuickBooks connection.",
  },
  normalized_name: {
    title: "Normalized Name",
    description: "Auto-generated lowercased / cleaned form of the company name used for matching and dedup.",
    usage: "Read-only — generated by the system. You never edit this; change Name and it follows.",
  },
  created_at: {
    title: "Created",
    description: "When the company record was created.",
    usage: "Read-only. Useful for account aging and cohorting which clients came on when.",
  },
  updated_at: {
    title: "Last Updated",
    description: "When the company was last modified.",
    usage: "Read-only. Surfaces the most recently touched accounts.",
  },
};
